import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import * as yup from 'yup';

import { createColumnHelper } from '@tanstack/react-table';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import DataTable from 'components/shared/DataTable';
import FormHeader from 'components/shared/form/FormHeader';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import { STATE_OPTIONS } from 'constants/states';
import { useProfile } from 'context/profile';
import { useAdminForm } from 'hooks/datasource';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { Location } from 'models/flexible/Location';
import { PERMISSIONS } from 'models/flexible/Profile';
import { adminIndexClients } from 'services/api/admin/clients';
import { adminIndexGroups } from 'services/api/admin/groups';
import {
  adminLocationsQueryKeys,
  adminShowLocation,
  createLocation,
  updateLocation,
} from 'services/api/admin/locations';
import type { AdminLocationShowFields } from 'services/api/fields/admin/location';
import { colors } from 'styles/theme/colors';
import TrashIcon from 'svg/TrashIcon';

type GroupRow = {
  id: string;
  name: string;
  groupType?: { id: string; displayName: string };
};

const getId = (o: any) => o!.id as string;
const getName = (o: any) => o!.name as string;

const schema = yup.object({
  name: yup.string().required('Location name required'),
  client: yup.mixed().test('client', 'Client is required', (v: any) => !!v?.id),
});

export default function EditLocation() {
  const { id } = useParams();
  const isEditing = !!id;
  const { profile } = useProfile();

  const form = useAdminForm({
    id,
    show: adminShowLocation,
    create: createLocation,
    update: updateLocation,
    include: ['groups'] as const,
    queryKey: adminLocationsQueryKeys,
    schema,
    toFormValues: (data: Location<AdminLocationShowFields<'groups'>> | undefined) => ({
      name: data?.name ?? '',
      externalId: data?.externalId ?? '',
      client: data?.client?.id ? data.client : (null as { id: string; name: string } | null),
      groups: data?.groups ?? [],
      address: {
        address1: data?.address?.address1 ?? '',
        address2: data?.address?.address2 ?? '',
        city: data?.address?.city ?? '',
        state: data?.address?.state
          ? { label: data.address.state, value: data.address.state }
          : (null as { label: string; value: string } | null),
        zip: data?.address?.zip ?? '',
      },
    }),
    toPayload: (v) => ({
      name: v.name,
      externalId: v.externalId || null,
      clientId: v.client?.id,
      groupIds: (v.groups || []).map((g: GroupRow) => g.id),
      address: {
        address1: v.address?.address1 || null,
        address2: v.address?.address2 || null,
        city: v.address?.city || null,
        state: v.address?.state?.value || null,
        zip: v.address?.zip || null,
      },
    }),
    onCreateSuccess: { toast: 'Location successfully added!', navigate: '/admin/locations' },
    onUpdateSuccess: { toast: 'Location successfully updated!', navigate: '/admin/locations' },
  });

  const canEdit = profile.has(PERMISSIONS.adminGroupEdit);
  const groups = useMemo(() => form.values.groups ?? [], [form.values.groups]);
  const initialGroupIdsRef = useRef<Set<string>>(new Set(groups.map((g) => g.id)));
  const [addedGroups, setAddedGroups] = useState<GroupRow[]>([]);

  const preExistingGroups = useMemo(() => groups.filter((g) => initialGroupIdsRef.current.has(g.id)), [groups]);
  const allGroups = useMemo(() => [...preExistingGroups, ...addedGroups], [preExistingGroups, addedGroups]);

  const clientOpts = useInfiniteOptions({
    queryKey: ['adminClients', 'locations', { leaf: true }],
    fetchFn: adminIndexClients,
    params: { leaf: true, sortBy: 'name asc' },
  });

  const groupOpts = useInfiniteOptions({
    queryKey: ['adminGroups', 'locations', form.values.client?.id],
    fetchFn: adminIndexGroups,
    params: { clientId: form.values.client?.id, include: 'group_type', sortBy: 'name asc' },
  });

  const handleClientChange = (client: any) => {
    form.setValue('client', client);
    setAddedGroups([]);
    form.setValue('groups', isEditing ? preExistingGroups : []);
  };

  const handleAddGroup = useCallback(
    (group: any) => {
      const row: GroupRow = { id: group.id, name: group.name, groupType: group.groupType };
      const updated = [...addedGroups, row];
      setAddedGroups(updated);
      form.setValue('groups', [...groups, row]);
    },
    [addedGroups, groups, form]
  );

  const handleRemoveGroup = useCallback(
    (groupId: string) => {
      setAddedGroups(addedGroups.filter((g) => g.id !== groupId));
      form.setValue(
        'groups',
        groups.filter((g) => g.id !== groupId)
      );
    },
    [addedGroups, groups, form]
  );

  const columnHelper = createColumnHelper<GroupRow>();
  const groupColumns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Group Name',
        cell: (data) => <BoldText>{data.getValue()}</BoldText>,
        enableSorting: false,
      }),
      columnHelper.accessor((row) => row.groupType?.displayName || '', {
        id: 'groupType',
        header: 'Group Type',
        cell: (data) => data.getValue(),
        enableSorting: false,
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: (data) => {
          const isNew = !initialGroupIdsRef.current.has(data.row.original.id);
          return isNew && canEdit ? (
            <IconContainer onClick={() => handleRemoveGroup(data.row.original.id)}>
              <TrashIcon style={{ color: colors.accentRed }} />
            </IconContainer>
          ) : null;
        },
        size: 40,
      }),
    ],
    [handleRemoveGroup, canEdit, columnHelper]
  );

  if (isEditing && form.loading) return <CenteredSpinner />;

  return (
    <AdminFormPage>
      <>
        <FormHeader title={isEditing ? 'Edit Location' : 'Add Location'} />

        <SectionHeader>Location Information</SectionHeader>

        <InputGroup title='Client' error={form.errors.client}>
          <Select
            {...clientOpts}
            value={form.values.client}
            onChange={handleClientChange}
            getOptionValue={getId}
            getOptionLabel={getName}
            placeholder='Client'
            isDisabled={!canEdit}
          />
        </InputGroup>

        <InputGroup title='Location Name' error={form.errors.name}>
          <Input
            value={form.values.name ?? ''}
            onChange={(e) => form.setValue('name', e.target.value)}
            placeholder='Location Name'
            $hasError={!!form.errors.name}
            disabled={!canEdit}
          />
        </InputGroup>

        <InputGroup title='External ID'>
          <Input
            value={form.values.externalId ?? ''}
            onChange={(e) => form.setValue('externalId', e.target.value)}
            placeholder='External ID'
            disabled={!canEdit}
          />
        </InputGroup>

        <SectionHeader>Groups</SectionHeader>

        <InputGroup title='Add Group'>
          <Select
            {...groupOpts}
            value={null}
            onChange={handleAddGroup}
            getOptionValue={getId}
            getOptionLabel={(o: any) => (o.groupType?.displayName ? `${o.name} (${o.groupType.displayName})` : o.name)}
            placeholder='Search for a group...'
            isDisabled={!form.values.client?.id || !canEdit}
          />
        </InputGroup>

        {allGroups.length > 0 && (
          <GroupsTableWrapper>
            <DataTable columns={groupColumns} data={allGroups} />
          </GroupsTableWrapper>
        )}

        <SectionHeader>Address</SectionHeader>

        <InlineGroup>
          <InputGroup title='Street 1'>
            <Input
              value={form.values.address?.address1 ?? ''}
              onChange={(e) => form.setValue('address.address1', e.target.value)}
              placeholder='Street 1'
              disabled={!canEdit}
            />
          </InputGroup>
          <InputGroup title='Street 2 (optional)'>
            <Input
              value={form.values.address?.address2 ?? ''}
              onChange={(e) => form.setValue('address.address2', e.target.value)}
              placeholder='Street 2'
              disabled={!canEdit}
            />
          </InputGroup>
        </InlineGroup>

        <InlineGroup>
          <InputGroup title='City'>
            <Input
              value={form.values.address?.city ?? ''}
              onChange={(e) => form.setValue('address.city', e.target.value)}
              placeholder='City'
              disabled={!canEdit}
            />
          </InputGroup>
          <InlineGroup>
            <InputGroup title='State'>
              <Select
                options={STATE_OPTIONS}
                value={form.values.address?.state}
                onChange={(v) => form.setValue('address.state', v)}
                getOptionValue={(o) => o.value}
                getOptionLabel={(o) => o.label}
                placeholder='State'
                isDisabled={!canEdit}
              />
            </InputGroup>
            <InputGroup title='Zip'>
              <Input
                value={form.values.address?.zip ?? ''}
                onChange={(e) => form.setValue('address.zip', e.target.value)}
                placeholder='Zip'
                disabled={!canEdit}
              />
            </InputGroup>
          </InlineGroup>
        </InlineGroup>

        <div style={{ display: 'flex', width: '100%' }}>
          {!isEditing && (
            <Button
              variant='outline-primary'
              onClick={() => form.submitAndReset(['name'])}
              disabled={!form.valid || form.submitting}
              loading={form.submitting}>
              Save and Add Another
            </Button>
          )}
          <div style={{ flex: 1 }} />
          <ButtonGroup>
            <Button variant='ghost' onClick={form.goBack}>
              Cancel
            </Button>
            <Button onClick={form.submit} disabled={!form.valid || form.submitting} loading={form.submitting}>
              {isEditing ? 'Update Location' : 'Create Location'}
            </Button>
          </ButtonGroup>
        </div>
      </>
    </AdminFormPage>
  );
}

const InlineGroup = styled.div`
  display: flex;
  gap: 16px;

  > * {
    flex: 1;
  }
`;

const GroupsTableWrapper = styled.div`
  margin-bottom: 16px;
`;

const IconContainer = styled.div`
  cursor: pointer;
  width: 16px;
`;

const BoldText = styled.span`
  font-weight: 700;
`;

const CenteredSpinner = styled(CircularProgress)`
  margin: 64px auto;
  display: block;
`;
